<template>
  <div class="kiosk-input-wrapper">
    <div
      class="slot-container"
      @focusin="onFocusIn"
      @focusout="onFocusOut"
    >
      <slot></slot>
    </div>

    <transition name="expand">
      <div
        v-if="showKeyboard"
        class="webkeyboard"
        @mousedown.prevent
      >
        <div v-for="(row, rIdx) in currentLayout" :key="rIdx" class="keyboard-row">
          <button
            v-for="key in row"
            :key="key"
            class="key-btn"
            :class="getKeyClass(key)"
            @click="handleKeyPress(key)"
          >
            {{ displayKey(key) }}
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'KioskInputWrapper',
  props: {
    value: {type: [String, Number], default: ""}
  },
  data() {
    return {
      showKeyboard: false,
      isShiftActive: false,
      isSymbolLayout: false,
      layouts: {
        normal: [
          ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0", "ß"],
          ["q", "w", "e", "r", "t", "z", "u", "i", "o", "p", "ü"],
          ["a", "s", "d", "f", "g", "h", "j", "k", "l", "ö", "ä"],
          ["SHIFT", "y", "x", "c", "v", "b", "n", "m", ",", ".", "-", "SYMBOLS"],
          ["UP", "DOWN", "LEERTASTE", "LINKS", "RECHTS", "LÖSCHEN", "ENTER", "FERTIG"]
        ],
        shift: [
          ["!", "\"", "§", "$", "%", "&", "/", "(", ")", "=", "?"],
          ["Q", "W", "E", "R", "T", "Z", "U", "I", "O", "P", "Ü"],
          ["A", "S", "D", "F", "G", "H", "J", "K", "L", "Ö", "Ä"],
          ["SHIFT_ACTIVE", "Y", "X", "C", "V", "B", "N", "M", ";", ":", "_", "SYMBOLS"],
          ["UP", "DOWN", "LEERTASTE", "LINKS", "RECHTS", "LÖSCHEN", "ENTER", "FERTIG"]
        ],
        symbols: [
          ["[", "]", "{", "}", "\\", "|", "<", ">", "€", "@", "~"],
          ["+", "*", "#", "'", "\"", "^", "°", "μ", "´", "`", "²", "SYMBOLS_ACTIVE"],
          ["UP", "DOWN", "LEERTASTE", "LINKS", "RECHTS", "LÖSCHEN", "ENTER", "FERTIG"]
        ]
      }
    };
  },
  computed: {
    currentLayout() {
      if (this.isSymbolLayout) return this.layouts.symbols;
      return this.isShiftActive ? this.layouts.shift : this.layouts.normal;
    }
  },
  methods: {
    onFocusIn() {
      this.showKeyboard = true;
    },
    onFocusOut(e) {
      if (!this.$el.contains(e.relatedTarget)) this.showKeyboard = false;
    },
    getKeyClass(key) {
      return {
        'special-key': ["LÖSCHEN", "ENTER", "FERTIG", "SHIFT", "SHIFT_ACTIVE", "SYMBOLS", "SYMBOLS_ACTIVE"].includes(key),
        'nav-key': ["LINKS", "RECHTS", "UP", "DOWN"].includes(key),
        'space-key': key === "LEERTASTE",
        'done-key': key === "FERTIG",
        'shift-active-key': key === "SHIFT_ACTIVE",
        'sym-active-key': key === "SYMBOLS_ACTIVE"
      };
    },
    displayKey(key) {
      const icons = {
        'LÖSCHEN': '⌫', 'ENTER': '↵', 'FERTIG': 'OK', 'LINKS': '◀', 'RECHTS': '▶',
        'UP': '▲', 'DOWN': '▼', 'SHIFT': '⇧', 'SHIFT_ACTIVE': '⇧', 'SYMBOLS': '?123',
        'SYMBOLS_ACTIVE': 'ABC', 'LEERTASTE': ' '
      };
      return icons[key] || key;
    },
    handleKeyPress(key) {
      const input = this.$el.querySelector("input, textarea");
      if (!input) return;

      let start = input.selectionStart;
      let end = input.selectionEnd;
      const currentVal = String(this.value || "");

      if (key === "FERTIG") {
        this.showKeyboard = false;
        input.blur();
        return;
      }
      if (key === "SHIFT" || key === "SHIFT_ACTIVE") {
        this.isShiftActive = !this.isShiftActive;
        return;
      }
      if (key === "SYMBOLS" || key === "SYMBOLS_ACTIVE") {
        this.isSymbolLayout = !this.isSymbolLayout;
        return;
      }

      // Cursor Navigation
      if (key === "LINKS") {
        this.moveCursor(input, start - 1);
        return;
      }
      if (key === "RECHTS") {
        this.moveCursor(input, start + 1);
        return;
      }
      if (key === "UP") {
        this.moveCursorVertical(input, -1);
        return;
      }
      if (key === "DOWN") {
        this.moveCursorVertical(input, 1);
        return;
      }

      if (key === "LÖSCHEN") {
        const newVal = (start === end && start > 0)
          ? currentVal.slice(0, start - 1) + currentVal.slice(end)
          : currentVal.slice(0, start) + currentVal.slice(end);
        this.emitUpdate(newVal, start === end ? start - 1 : start, input);
      } else {
        const char = key === "ENTER" ? "\n" : (key === "LEERTASTE" ? " " : key);
        const newVal = currentVal.slice(0, start) + char + currentVal.slice(end);
        this.emitUpdate(newVal, start + char.length, input);

        // Auto-Shift Back
        if (this.isShiftActive) this.isShiftActive = false;
      }
    },
    moveCursorVertical(input, dir) {
      if (input.tagName !== 'TEXTAREA') return;
      const text = input.value;
      const pos = input.selectionStart;
      const lines = text.split('\n');
      let curLine = 0, curStart = 0;
      for (let i = 0; i < lines.length; i++) {
        let end = curStart + lines[i].length;
        if (pos >= curStart && pos <= end) {
          curLine = i;
          break;
        }
        curStart = end + 1;
      }
      let targetLine = curLine + dir;
      if (targetLine < 0 || targetLine >= lines.length) return;
      let targetStart = 0;
      for (let i = 0; i < targetLine; i++) targetStart += lines[i].length + 1;
      const newPos = targetStart + Math.min(pos - curStart, lines[targetLine].length);
      input.setSelectionRange(newPos, newPos);
    },
    moveCursor(input, pos) {
      const p = Math.max(0, Math.min(pos, String(this.value).length));
      input.setSelectionRange(p, p);
      input.focus();
    },
    emitUpdate(val, pos, input) {
      this.$emit("input", val);
      this.$nextTick(() => {
        input.setSelectionRange(pos, pos);
        input.focus();
      });
    }
  }
};
</script>

<style scoped>
.kiosk-input-wrapper {
  width: 100%;
  margin-bottom: 10px;
}

.webkeyboard {
  background: #eeeeee;
  border: 1px solid #999;
  border-radius: 10px;
  padding: 10px;
  margin-top: 5px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.keyboard-row {
  display: flex;
  justify-content: center;
  gap: 4px;
  margin-bottom: 5px;
}

.key-btn {
  height: 55px;
  min-width: 42px;
  background: #fff;
  border: 1px solid #bbb;
  border-radius: 5px;
  font-weight: bold;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 0 #999;
}

.key-btn:active {
  background: #ddd;
  transform: translateY(1px);
  box-shadow: none;
}

.special-key {
  background: #d1d1d1;
  min-width: 60px;
  font-size: 14px;
}

.nav-key {
  background: #fff9c4;
  font-size: 20px;
  min-width: 50px;
}

.done-key {
  background: #4caf50;
  color: white;
  min-width: 70px;
}

.shift-active-key, .sym-active-key {
  background: #90a4ae;
  color: white;
}

/* Die wichtige Leertasten-Klasse */
.space-key {
  min-width: 180px;
  background: #ffffff;
}

.expand-enter-active, .expand-leave-active {
  transition: all 0.25s ease;
  max-height: 400px;
}

.expand-enter, .expand-leave-to {
  max-height: 0;
  opacity: 0;
}
</style>
